import React, { useState } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import {
  Box,
  Typography,
  TextField,
  Snackbar,
  Link,
  IconButton,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  MobileStepper,
  useTheme,
  useMediaQuery,
  Divider,
  Avatar,
  Fade,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
  Groups as GroupsIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import Alert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";

import Bugsnag from "@bugsnag/js";
import InputAdornment from "@mui/material/InputAdornment";
import NewColors from "../../themes/NewColors";
import logoImage from "../../assets/Logo.png";
import useEvents from "../../hooks/useEvents";

// Styled Components following Mighty45 branding patterns
const OnboardingContainer = styled(Box)(({ theme }) => ({
  backgroundColor: NewColors.pageBackground,
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(3),
}));

const OnboardingCard = styled(Card)(({ theme }) => ({
  maxWidth: 600,
  width: "100%",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
  backgroundColor: NewColors.cardBackground,
  overflow: "visible",
}));

const CardHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  paddingBottom: theme.spacing(2),
  textAlign: "center",
}));

const CardContentWrapper = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(0, 4, 4, 4),
  "&:last-child": {
    paddingBottom: theme.spacing(4),
  },
}));

const BrandTitle = styled(Typography)(({ theme }) => ({
  color: NewColors.text.primary,
  fontWeight: "bold",
  marginBottom: theme.spacing(1),
  fontFamily: "Lato, sans-serif",
}));

const StepTitle = styled(Typography)(({ theme }) => ({
  color: NewColors.text.primary,
  fontWeight: "bold",
  marginBottom: theme.spacing(2),
  fontFamily: "Lato, sans-serif",
}));

const FormField = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1),
    backgroundColor: NewColors.cardBackground,
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: NewColors.active.primary,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: NewColors.active.primary,
    },
  },
}));

const ProgressStepper = styled(Stepper)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  "& .MuiStepIcon-root": {
    color: theme.palette.grey[300],
    "&.Mui-active": {
      color: NewColors.active.primary,
    },
    "&.Mui-completed": {
      color: NewColors.brand.primary,
    },
  },
}));

const BenefitCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: NewColors.pageBackground,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const StepperButton = styled(Button)(({ theme, direction }) => ({
  backgroundColor: direction === "back" ? "transparent" : NewColors.brand.primary,
  color: direction === "back" ? NewColors.active.primary : NewColors.cardBackground,
  fontWeight: 600,
  borderRadius: theme.spacing(1),
  textTransform: "none",
  margin: theme.spacing(1),
  borderColor: direction === "back" ? NewColors.active.primary : "transparent",
  border: direction === "back" ? "1px solid" : "none",
  "&:hover": {
    backgroundColor:
      direction === "back" ? NewColors.triadicPower.interactive.hover : NewColors.status.error,
  },
}));

const StepIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: NewColors.brand.primary,
  color: NewColors.cardBackground,
  width: theme.spacing(6),
  height: theme.spacing(6),
  marginBottom: theme.spacing(2),
  boxShadow: "0 3px 10px rgba(196, 15, 13, 0.2)",
}));

const steps = ["Account Setup", "Personal Info", "Organization"];
const OnboardingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [organizationDescription, setOrganizationDescription] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleNext = () => {
    if (activeStep === 0) {
      if (!email || !password) {
        showError("Please complete all fields before continuing.");
        return;
      }
    } else if (activeStep === 1) {
      if (!firstName || !lastName) {
        showError("Please complete all fields before continuing.");
        return;
      }
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const validate = () => {
    if (firstName.trim().length <= 0) {
      return "Enter your first name to continue.";
    }
    if (lastName.trim().length <= 0) {
      return "Enter your last name to continue.";
    }
    if (organizationName.trim().length <= 0) {
      return "Enter the name of your organization to continue.";
    }
    if (email.trim().length <= 0) {
      return "Enter your email address to continue.";
    }
    if (password.trim().length <= 0) {
      return "You need a password to continue.";
    }
    return null;
  };

  async function handleOnboarding() {
    const validation = validate();
    if (validation) {
      showError(validation);
      return;
    }

    setIsCreating(true);

    try {
      // Call the atomic organization creation API
      const timestamp = Date.now().toString();
      const systemKey = 'M45_SYSTEM_KEY_2024';
      const signature = `${systemKey}_${timestamp}`;

      const response = await fetch("https://app.mighty45.com/api/v1/organization_register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-mighty45-system-key": systemKey,
          "x-mighty45-timestamp": timestamp,
          "x-mighty45-signature": signature,
        },
        body: JSON.stringify({
          email: email,
          password: password,
          userData: {
            firstName: firstName,
            lastName: lastName,
          },
          organizationData: {
            name: organizationName,
            description: organizationDescription,
          },
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Store user and organization data in localStorage
        localStorage.setItem("uid", result.user.uid);
        localStorage.setItem("organizationId", result.organization.id);
        
        // Authenticate with Firebase using the created credentials
        try {
          await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
          await firebase.auth().signInWithEmailAndPassword(email, password);
          
          // Navigate to home page after successful authentication
          navigate("/home");
          setIsCreating(false);
        } catch (authError) {
          Bugsnag.notify(authError);
          showError("Organization created successfully, but there was an authentication issue. Please try signing in manually.");
          setIsCreating(false);
        }
      } else {
        showError(result.error || "There was an error starting the organization.");
        Bugsnag.notify(new Error(`Organization creation failed: ${result.error}`));
        setIsCreating(false);
      }
    } catch (e) {
      setIsCreating(false);
      Bugsnag.notify(e);
      showError(`There was an error starting the organization. ${e.message}`);
    }
  }

  function showError(message) {
    setErrorMessage(message);
    setErrorOpen(true);
  }

  function clearError() {
    setErrorMessage("");
    setErrorOpen(false);
  }

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  function getStepContent(step) {
    switch (step) {
      case 0:
        return (
          <Fade in={activeStep === 0}>
            <Box>
              <StepIcon stepNumber={1}>
                <LockIcon />
              </StepIcon>
              <StepTitle variant="h4">Account Setup</StepTitle>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Let&apos;s start by creating your secure login credentials
              </Typography>

              <FormField>
                <StyledTextField
                  fullWidth
                  label="Email Address"
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </FormField>

              <FormField>
                <StyledTextField
                  fullWidth
                  label="Password"
                  variant="outlined"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormField>

              <BenefitCard>
                <SecurityIcon sx={{ color: NewColors.active.primary }} />
                <Typography variant="body2">
                  Your data is securely encrypted and protected
                </Typography>
              </BenefitCard>
            </Box>
          </Fade>
        );
      case 1:
        return (
          <Fade in={activeStep === 1}>
            <Box>
              <StepIcon stepNumber={2}>
                <PersonIcon />
              </StepIcon>
              <StepTitle variant="h4">Personal Information</StepTitle>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Tell us a bit about yourself so we can personalize your
                experience
              </Typography>

              <FormField>
                <StyledTextField
                  fullWidth
                  label="First Name"
                  variant="outlined"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </FormField>

              <FormField>
                <StyledTextField
                  fullWidth
                  label="Last Name"
                  variant="outlined"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </FormField>

              <BenefitCard>
                <TrendingUpIcon sx={{ color: NewColors.brand.primary }} />
                <Typography variant="body2">
                  Your profile helps build stronger client relationships
                </Typography>
              </BenefitCard>
            </Box>
          </Fade>
        );
      case 2:
        return (
          <Fade in={activeStep === 2}>
            <Box>
              <StepIcon stepNumber={3}>
                <BusinessIcon />
              </StepIcon>
              <StepTitle variant="h4">Organization Details</StepTitle>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Set up your coaching business on Mighty45
              </Typography>

              <FormField>
                <StyledTextField
                  fullWidth
                  label="Organization Name"
                  variant="outlined"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </FormField>

              <FormField>
                <StyledTextField
                  fullWidth
                  label="Organization Slogan/Description"
                  variant="outlined"
                  value={organizationDescription}
                  onChange={(e) => setOrganizationDescription(e.target.value)}
                />
              </FormField>

              <BenefitCard>
                <GroupsIcon sx={{ color: NewColors.active.primary }} />
                <Typography variant="body2">
                  Manage multiple clients and their training plans in one place
                </Typography>
              </BenefitCard>

              <BenefitCard>
                <ScheduleIcon sx={{ color: NewColors.brand.primary }} />
                <Typography variant="body2">
                  Start your 30-day free trial with all premium features
                  unlocked
                </Typography>
              </BenefitCard>
            </Box>
          </Fade>
        );
      default:
        return "Unknown step";
    }
  }

  // Custom Footer for each step
  function getStepActions(step) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          mt: 4,
        }}
      >
        <Box>
          {step > 0 && (
            <StepperButton
              direction="back"
              variant="outlined"
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              disabled={isCreating}
            >
              Back
            </StepperButton>
          )}
        </Box>
        <Box>
          {step === steps.length - 1 ? (
            <StepperButton
              direction="forward"
              variant="contained"
              onClick={() => {
                handleOnboarding();
                useEvents().logButtonClick({
                  buttonName: "Create Organization",
                  pageName: "Onboarding",
                });
              }}
              endIcon={<CheckCircleIcon />}
              disabled={isCreating}
            >
              Create Organization
            </StepperButton>
          ) : (
            <StepperButton
              direction="forward"
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForwardIcon />}
              disabled={isCreating}
            >
              Continue
            </StepperButton>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <OnboardingContainer>
      <OnboardingCard>
        <CardHeader>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <a href="https://mighty45.com">
              <img
                alt="Mighty45 Logo"
                style={{ maxWidth: "100%", height: "auto" }}
                width="140px"
                src={logoImage}
              />
            </a>
          </Box>
          <BrandTitle variant="h4">Join Mighty45</BrandTitle>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Streamline your coaching business in minutes
          </Typography>

          {!isMobile ? (
            <ProgressStepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </ProgressStepper>
          ) : (
            <MobileStepper
              variant="dots"
              steps={steps.length}
              position="static"
              activeStep={activeStep}
              sx={{
                background: "transparent",
                width: "100%",
                justifyContent: "center",
                mb: 2,
              }}
              nextButton={null}
              backButton={null}
            />
          )}
          <Divider sx={{ mt: 1, mb: 4 }} />
        </CardHeader>

        <CardContentWrapper>
          {getStepContent(activeStep)}
          {getStepActions(activeStep)}

          {isCreating && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: 4,
              }}
            >
              <CircularProgress size={40} sx={{ color: NewColors.brand.primary }} />
              <Typography variant="body1" sx={{ mt: 2, textAlign: "center" }}>
                Building your organization. Please wait...
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 4, textAlign: "center" }}>
            {activeStep === 0 && (
              <Typography variant="body2" color="text.secondary">
                Already have an account?{" "}
                <Link href="/signin" color={NewColors.active.primary}>
                  Sign in
                </Link>
              </Typography>
            )}

            {activeStep === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Need to join an existing organization?{" "}
                <Link href="/connect" color={NewColors.active.primary}>
                  Connect here
                </Link>
              </Typography>
            )}
          </Box>
        </CardContentWrapper>
      </OnboardingCard>

      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={() => {
          clearError();
        }}
      >
        <Alert
          onClose={() => {
            clearError();
          }}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </OnboardingContainer>
  );
};

export default OnboardingPage;
